import React, { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ClipboardCheck, RefreshCw, Search, ShieldAlert, UserRoundCheck } from 'lucide-react';
import api from '../lib/api';

type ApplicationType = 'RENTAL_TENANT' | 'PROPERTY_SELLER';
type ApplicationStatus = 'PENDING_APPROVAL' | 'APPROVED' | 'REJECTED';
type BackgroundCheckStatus = 'NOT_STARTED' | 'UNDER_REVIEW' | 'CLEAR' | 'RESTRICTED';

interface PetData {
  name?: string;
  type?: string;
  breed?: string;
  notes?: string;
}

interface OnboardingApplication {
  id: string;
  applicationType: ApplicationType;
  status: ApplicationStatus;
  fullName: string;
  email: string;
  phone?: string | null;
  cpf?: string | null;
  rg?: string | null;
  driverLicense?: string | null;
  motherName?: string | null;
  maritalStatus?: string | null;
  spouseName?: string | null;
  childrenNames: string[];
  stepchildrenNames: string[];
  relativesNotes?: string | null;
  hasPets: boolean;
  pets?: unknown;
  zipCode: string;
  street: string;
  number: string;
  complement?: string | null;
  neighborhood: string;
  city: string;
  state: string;
  employmentInfo?: string | null;
  monthlyIncome?: string | number | null;
  intendedOccupants?: string | null;
  rentalGuaranteePreference?: string | null;
  propertyRegistrationNumber?: string | null;
  hasActiveDebt?: boolean | null;
  activeDebtDetails?: string | null;
  isInPartition?: boolean | null;
  isInInventory?: boolean | null;
  legalIssuesDetails?: string | null;
  propertyDocumentationNotes?: string | null;
  additionalComplianceInfo?: string | null;
  spcStatus: BackgroundCheckStatus;
  serasaStatus: BackgroundCheckStatus;
  justiceStatus: BackgroundCheckStatus;
  backgroundCheckNotes?: string | null;
  reviewNotes?: string | null;
  approvedAt?: string | null;
  rejectedAt?: string | null;
  createdAt: string;
}

interface ApiError {
  response?: {
    data?: {
      message?: string | string[];
    };
  };
}

function parseError(error: unknown, fallback: string): string {
  const typedError = error as ApiError;
  const message = typedError?.response?.data?.message;
  if (Array.isArray(message)) return message.join(', ');
  if (typeof message === 'string' && message.trim().length > 0) return message;
  return fallback;
}

function formatDate(value?: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('pt-BR');
}

function formatCurrency(value?: string | number | null): string {
  if (value === null || value === undefined || value === '') return '-';
  const numberValue = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numberValue)) return '-';
  return numberValue.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

function parsePets(input: unknown): PetData[] {
  if (!Array.isArray(input)) return [];
  return input
    .map((item): PetData => {
      if (typeof item !== 'object' || item === null) return {};
      const pet = item as Record<string, unknown>;
      return {
        name: typeof pet.name === 'string' ? pet.name : undefined,
        type: typeof pet.type === 'string' ? pet.type : undefined,
        breed: typeof pet.breed === 'string' ? pet.breed : undefined,
        notes: typeof pet.notes === 'string' ? pet.notes : undefined,
      };
    })
    .filter((pet) => Boolean(pet.type));
}

const typeLabels: Record<ApplicationType, string> = {
  RENTAL_TENANT: 'Locacao - Locatario',
  PROPERTY_SELLER: 'Venda - Proprietario',
};

const statusLabels: Record<ApplicationStatus, string> = {
  PENDING_APPROVAL: 'Pendente',
  APPROVED: 'Aprovado',
  REJECTED: 'Reprovado',
};

const checkStatusLabels: Record<BackgroundCheckStatus, string> = {
  NOT_STARTED: 'Nao iniciado',
  UNDER_REVIEW: 'Em analise',
  CLEAR: 'Sem restricao',
  RESTRICTED: 'Com restricao',
};

const statusClasses: Record<ApplicationStatus, string> = {
  PENDING_APPROVAL: 'bg-yellow-100 text-yellow-800',
  APPROVED: 'bg-green-100 text-green-800',
  REJECTED: 'bg-red-100 text-red-800',
};

export default function OnboardingCompliancePage() {
  const [applications, setApplications] = useState<OnboardingApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | ApplicationStatus>('ALL');
  const [typeFilter, setTypeFilter] = useState<'ALL' | ApplicationType>('ALL');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reviewStatus, setReviewStatus] = useState<'APPROVED' | 'REJECTED'>('APPROVED');
  const [reviewNotes, setReviewNotes] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [savingChecks, setSavingChecks] = useState(false);
  const [checksForm, setChecksForm] = useState<{
    spcStatus: BackgroundCheckStatus;
    serasaStatus: BackgroundCheckStatus;
    justiceStatus: BackgroundCheckStatus;
    backgroundCheckNotes: string;
  }>({
    spcStatus: 'NOT_STARTED',
    serasaStatus: 'NOT_STARTED',
    justiceStatus: 'NOT_STARTED',
    backgroundCheckNotes: '',
  });

  const selectedApplication = useMemo(
    () => applications.find((app) => app.id === selectedId) ?? null,
    [applications, selectedId],
  );

  const loadApplications = async (overrideSearch?: string) => {
    setLoading(true);
    setError('');

    try {
      const params: Record<string, string | number> = {
        page: 1,
        limit: 100,
      };

      const effectiveSearch = overrideSearch ?? searchTerm;

      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (typeFilter !== 'ALL') params.applicationType = typeFilter;
      if (effectiveSearch.trim()) params.search = effectiveSearch.trim();

      const response = await api.get('/customer-onboarding/applications', { params });
      const nextApps = Array.isArray(response.data?.data) ? response.data.data : [];
      setApplications(nextApps);

      if (nextApps.length === 0) {
        setSelectedId(null);
        return;
      }

      const hasSelected = selectedId
        ? nextApps.some((item: OnboardingApplication) => item.id === selectedId)
        : false;

      if (!hasSelected) {
        setSelectedId(nextApps[0].id);
      }
    } catch (loadError: unknown) {
      setError(parseError(loadError, 'Nao foi possivel carregar os cadastros.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadApplications();
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    if (!selectedApplication) return;
    setReviewStatus(selectedApplication.status === 'REJECTED' ? 'REJECTED' : 'APPROVED');
    setReviewNotes(selectedApplication.reviewNotes ?? '');
    setChecksForm({
      spcStatus: selectedApplication.spcStatus,
      serasaStatus: selectedApplication.serasaStatus,
      justiceStatus: selectedApplication.justiceStatus,
      backgroundCheckNotes: selectedApplication.backgroundCheckNotes ?? '',
    });
  }, [selectedApplication]);

  const handleSearch = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await loadApplications(searchTerm);
  };

  const handleUpdateStatus = async () => {
    if (!selectedApplication) return;
    setSavingStatus(true);
    setError('');

    try {
      await api.patch(`/customer-onboarding/applications/${selectedApplication.id}/status`, {
        status: reviewStatus,
        reviewNotes,
      });
      await loadApplications();
    } catch (updateError: unknown) {
      setError(parseError(updateError, 'Nao foi possivel atualizar o status.'));
    } finally {
      setSavingStatus(false);
    }
  };

  const handleUpdateChecks = async () => {
    if (!selectedApplication) return;
    setSavingChecks(true);
    setError('');

    try {
      await api.patch(
        `/customer-onboarding/applications/${selectedApplication.id}/background-checks`,
        checksForm,
      );
      await loadApplications();
    } catch (updateError: unknown) {
      setError(parseError(updateError, 'Nao foi possivel atualizar as consultas.'));
    } finally {
      setSavingChecks(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold">Compliance de Cadastros</h1>
          <p className="text-gray-600 mt-1">
            Aprovacao administrativa de locatarios e proprietarios vendedores.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadApplications()}
          className="btn-secondary flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Atualizar
        </button>
      </div>

      <div className="card space-y-4">
        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Buscar por nome, e-mail, CPF ou telefone"
              className="input pl-9"
            />
          </div>
          <select
            className="input"
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value as 'ALL' | ApplicationStatus)
            }
          >
            <option value="ALL">Todos os status</option>
            <option value="PENDING_APPROVAL">Pendentes</option>
            <option value="APPROVED">Aprovados</option>
            <option value="REJECTED">Reprovados</option>
          </select>
          <select
            className="input"
            value={typeFilter}
            onChange={(event) => setTypeFilter(event.target.value as 'ALL' | ApplicationType)}
          >
            <option value="ALL">Todos os tipos</option>
            <option value="RENTAL_TENANT">Locacao</option>
            <option value="PROPERTY_SELLER">Venda</option>
          </select>
          <button type="submit" className="btn-primary md:col-span-4">
            Aplicar Filtros
          </button>
        </form>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="card xl:col-span-1">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-primary-600" />
            Cadastros
          </h2>

          {loading ? (
            <p className="text-gray-500">Carregando...</p>
          ) : applications.length === 0 ? (
            <p className="text-gray-500">Nenhum cadastro encontrado.</p>
          ) : (
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              {applications.map((application) => (
                <button
                  type="button"
                  key={application.id}
                  onClick={() => setSelectedId(application.id)}
                  className={`w-full text-left border rounded-lg p-3 transition-colors ${
                    application.id === selectedId
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-gray-900">{application.fullName}</p>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${statusClasses[application.status]}`}
                    >
                      {statusLabels[application.status]}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{typeLabels[application.applicationType]}</p>
                  <p className="text-sm text-gray-600 mt-1">{application.email}</p>
                  <p className="text-xs text-gray-500 mt-2">
                    Enviado em {formatDate(application.createdAt)}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="card xl:col-span-2">
          {!selectedApplication ? (
            <div className="h-full min-h-[280px] flex items-center justify-center text-gray-500">
              Selecione um cadastro para analisar.
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-bold">{selectedApplication.fullName}</h2>
                  <p className="text-sm text-gray-600">
                    {typeLabels[selectedApplication.applicationType]} • {selectedApplication.email}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-medium ${statusClasses[selectedApplication.status]}`}
                >
                  {statusLabels[selectedApplication.status]}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="border rounded-lg p-4 space-y-1">
                  <p className="font-semibold text-gray-900">Documentacao pessoal</p>
                  <p>Telefone: {selectedApplication.phone || '-'}</p>
                  <p>CPF: {selectedApplication.cpf || '-'}</p>
                  <p>RG: {selectedApplication.rg || '-'}</p>
                  <p>CNH: {selectedApplication.driverLicense || '-'}</p>
                  <p>Nome da mae: {selectedApplication.motherName || '-'}</p>
                </div>

                <div className="border rounded-lg p-4 space-y-1">
                  <p className="font-semibold text-gray-900">Endereco</p>
                  <p>
                    CEP: {selectedApplication.zipCode} • {selectedApplication.street},{' '}
                    {selectedApplication.number}
                  </p>
                  <p>
                    {selectedApplication.neighborhood} - {selectedApplication.city}/
                    {selectedApplication.state}
                  </p>
                  <p>Complemento: {selectedApplication.complement || '-'}</p>
                </div>

                <div className="border rounded-lg p-4 space-y-1">
                  <p className="font-semibold text-gray-900">Familia e relacionamentos</p>
                  <p>Estado civil: {selectedApplication.maritalStatus || '-'}</p>
                  <p>Conjuge: {selectedApplication.spouseName || '-'}</p>
                  <p>
                    Filhos:{' '}
                    {selectedApplication.childrenNames?.length
                      ? selectedApplication.childrenNames.join(', ')
                      : '-'}
                  </p>
                  <p>
                    Enteados:{' '}
                    {selectedApplication.stepchildrenNames?.length
                      ? selectedApplication.stepchildrenNames.join(', ')
                      : '-'}
                  </p>
                  <p>Parentes: {selectedApplication.relativesNotes || '-'}</p>
                </div>

                <div className="border rounded-lg p-4 space-y-1">
                  <p className="font-semibold text-gray-900">Pets</p>
                  <p>Possui pets: {selectedApplication.hasPets ? 'Sim' : 'Nao'}</p>
                  {parsePets(selectedApplication.pets).length ? (
                    <div className="space-y-1">
                      {parsePets(selectedApplication.pets).map((pet, index) => (
                        <p key={`${pet.type}-${index}`}>
                          {pet.name ? `${pet.name} - ` : ''}
                          {pet.type}
                          {pet.breed ? ` (${pet.breed})` : ''}
                          {pet.notes ? ` - ${pet.notes}` : ''}
                        </p>
                      ))}
                    </div>
                  ) : (
                    <p>-</p>
                  )}
                </div>
              </div>

              {selectedApplication.applicationType === 'RENTAL_TENANT' ? (
                <div className="border rounded-lg p-4 space-y-1 text-sm">
                  <p className="font-semibold text-gray-900">Dados para locacao</p>
                  <p>Renda mensal: {formatCurrency(selectedApplication.monthlyIncome)}</p>
                  <p>Profissao/empresa: {selectedApplication.employmentInfo || '-'}</p>
                  <p>Quem vai ocupar o imovel: {selectedApplication.intendedOccupants || '-'}</p>
                  <p>
                    Garantia preferida: {selectedApplication.rentalGuaranteePreference || '-'}
                  </p>
                </div>
              ) : (
                <div className="border rounded-lg p-4 space-y-1 text-sm">
                  <p className="font-semibold text-gray-900">Dados para venda</p>
                  <p>Matricula: {selectedApplication.propertyRegistrationNumber || '-'}</p>
                  <p>Divida ativa: {selectedApplication.hasActiveDebt ? 'Sim' : 'Nao'}</p>
                  <p>Detalhes da divida: {selectedApplication.activeDebtDetails || '-'}</p>
                  <p>Em partilha: {selectedApplication.isInPartition ? 'Sim' : 'Nao'}</p>
                  <p>Em inventario: {selectedApplication.isInInventory ? 'Sim' : 'Nao'}</p>
                  <p>Pendencias judiciais: {selectedApplication.legalIssuesDetails || '-'}</p>
                  <p>Documentacao: {selectedApplication.propertyDocumentationNotes || '-'}</p>
                </div>
              )}

              <div className="border rounded-lg p-4 space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                    <UserRoundCheck className="w-4 h-4 text-primary-600" />
                    Aprovacao administrativa
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    So apos aprovacao e permitido registrar resultado de SPC/Serasa/Justica.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <select
                    className="input"
                    value={reviewStatus}
                    onChange={(event) =>
                      setReviewStatus(event.target.value as 'APPROVED' | 'REJECTED')
                    }
                  >
                    <option value="APPROVED">Aprovar cadastro</option>
                    <option value="REJECTED">Reprovar cadastro</option>
                  </select>
                  <textarea
                    className="input md:col-span-2"
                    rows={2}
                    value={reviewNotes}
                    onChange={(event) => setReviewNotes(event.target.value)}
                    placeholder="Observacoes da analise administrativa"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => void handleUpdateStatus()}
                  disabled={savingStatus}
                  className="btn-primary w-full md:w-auto"
                >
                  {savingStatus ? 'Salvando...' : 'Salvar aprovacao/reprovacao'}
                </button>
              </div>

              <div className="border rounded-lg p-4 space-y-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                  Consultas de compliance
                </h3>

                {selectedApplication.status !== 'APPROVED' && (
                  <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded-lg text-sm flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4" />
                    Cadastro ainda nao aprovado. A atualizacao das consultas pode ser bloqueada.
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <select
                    className="input"
                    value={checksForm.spcStatus}
                    onChange={(event) =>
                      setChecksForm((prev) => ({
                        ...prev,
                        spcStatus: event.target.value as BackgroundCheckStatus,
                      }))
                    }
                  >
                    {Object.entries(checkStatusLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        SPC: {label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="input"
                    value={checksForm.serasaStatus}
                    onChange={(event) =>
                      setChecksForm((prev) => ({
                        ...prev,
                        serasaStatus: event.target.value as BackgroundCheckStatus,
                      }))
                    }
                  >
                    {Object.entries(checkStatusLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        Serasa: {label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="input"
                    value={checksForm.justiceStatus}
                    onChange={(event) =>
                      setChecksForm((prev) => ({
                        ...prev,
                        justiceStatus: event.target.value as BackgroundCheckStatus,
                      }))
                    }
                  >
                    {Object.entries(checkStatusLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        Justica: {label}
                      </option>
                    ))}
                  </select>
                </div>

                <textarea
                  className="input"
                  rows={3}
                  value={checksForm.backgroundCheckNotes}
                  onChange={(event) =>
                    setChecksForm((prev) => ({
                      ...prev,
                      backgroundCheckNotes: event.target.value,
                    }))
                  }
                  placeholder="Observacoes das consultas externas (SPC, Serasa, Justica, etc.)"
                />

                <button
                  type="button"
                  onClick={() => void handleUpdateChecks()}
                  disabled={savingChecks}
                  className="btn-primary w-full md:w-auto"
                >
                  {savingChecks ? 'Salvando...' : 'Salvar consultas'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
